import React from 'react';
import PropTypes from 'prop-types';
import HealthChip, { HEALTH_TONE } from './HealthChip';
import TimestampCell from './TimestampCell';
import CoverageBar from './CoverageBar';
import { formatNumber } from './datasetMonitorFormat';

const DEFAULT_TONE = 'grey';

/**
 * One number-plus-label pair in the Flow cell. Split out so the zero-dimming
 * rule lives in exactly one place rather than being repeated three times.
 */
const FlowValue = ({ value, label }) => (
	<>
		<span className={`flow-value${value === 0 ? ' is-zero' : ''}`}>
			{formatNumber(value)}
		</span>{' '}
		<span className="flow-label">{label}</span>
	</>
);

FlowValue.propTypes = {
	value: PropTypes.number.isRequired,
	label: PropTypes.string.isRequired,
};

/**
 * DatasetRow
 *
 * One dataset x source row: Dataset (health dot) | New data added | Current
 * data changed | Coverage | Flow | Alert email | Notification email |
 * Reminder email | Status.
 *
 * Flow shows ingested -> emailed only. The suppressed count was dropped at the
 * client's request as a term they could not interpret; the backend still
 * reports it and DatasetHealthEvaluator still reads it, so rule 3 is
 * unaffected - only its on-screen explanation is gone.
 *
 * Reminder sits here at the client's request, overriding design spec 12's
 * eight-column layout, which had moved it into the drawer. Expiring stays in
 * the drawer, which still shows all four email types in full.
 */
const DatasetRow = ({ dataset, onSelect }) => {
	const coverage = dataset.coverage || {};
	const ingested = dataset.ingested || {};
	const emails = dataset.emails || {};
	const alert = emails.alert || {};
	const notification = emails.notification || {};
	const reminder = emails.reminder || {};
	const failingProperties = dataset.runs?.failing_properties || 0;
	const tone = HEALTH_TONE[dataset.health] || DEFAULT_TONE;

	const handleSelect = (event) => {
		event.preventDefault();
		onSelect(dataset.key);
	};

	return (
		<tr className={`dataset-row row-${tone}`}>
			<td>
				<button
					type="button"
					onClick={handleSelect}
					className="btn btn-link p-0 dataset-link text-start"
					title={dataset.health_reason || undefined}
				>
					<span className={`health-dot chip-${tone}`} aria-hidden="true" />
					{dataset.label}
				</button>
			</td>
			<td>
				<TimestampCell iso={dataset.last_record_added_at} />
			</td>
			<td>
				<TimestampCell iso={dataset.last_record_changed_at} />
			</td>
			<td>
				<CoverageBar ran={coverage.ran} expected={coverage.expected} />
				{failingProperties > 0 && (
					<span
						className="badge bg-danger dataset-failing-badge"
						title="Properties whose latest run in the window failed"
					>
						{formatNumber(failingProperties)} failing
					</span>
				)}
			</td>
			<td className="dataset-flow-cell">
				{/* Numbers carry the meaning here; the words are labels. A zero is
				    dimmed rather than bolded, so a row where something actually
				    happened stands out from the forty where nothing did. */}
				<FlowValue value={ingested.added || 0} label="ingested" />
				<span className="flow-label mx-1" aria-hidden="true">
					&rarr;
				</span>
				{/* Window-scoped, matching the ingested count beside it. The
				    per-type counts in the drawer are all-time; pairing one of
				    those with a 24h ingest count read as "we emailed 80 of
				    today's 133" when the sends were spread over months. */}
				<FlowValue value={dataset.emails_sent_window || 0} label="emailed" />
			</td>
			<td>
				<TimestampCell
					iso={alert.last_sent_at}
					notApplicable={!dataset.emits_emails}
				/>
				{alert.failed > 0 && (
					<span className="ms-1 badge bg-danger">{alert.failed} failed</span>
				)}
			</td>
			<td>
				<TimestampCell
					iso={notification.last_sent_at}
					notApplicable={!dataset.emits_emails}
				/>
				{notification.failed > 0 && (
					<span className="ms-1 badge bg-danger">
						{notification.failed} failed
					</span>
				)}
			</td>
			<td>
				<TimestampCell
					iso={reminder.last_sent_at}
					notApplicable={!dataset.emits_emails}
				/>
				{reminder.failed > 0 && (
					<span className="ms-1 badge bg-danger">{reminder.failed} failed</span>
				)}
			</td>
			<td>
				<HealthChip health={dataset.health} reason={dataset.health_reason} />
			</td>
		</tr>
	);
};

DatasetRow.propTypes = {
	dataset: PropTypes.shape({
		key: PropTypes.string.isRequired,
		label: PropTypes.string,
		health: PropTypes.string,
		health_reason: PropTypes.string,
		emits_emails: PropTypes.bool,
		last_record_added_at: PropTypes.string,
		last_record_changed_at: PropTypes.string,
		coverage: PropTypes.object,
		ingested: PropTypes.object,
		emails: PropTypes.object,
		runs: PropTypes.object,
	}).isRequired,
	onSelect: PropTypes.func.isRequired,
};

export default DatasetRow;
